import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { X, Trash2, ExternalLink, Trophy, Share2, Printer } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { fetchCompare, trackJoinClick } from "@/lib/api";
import { useCompare } from "@/context/CompareContext";

const ROWS = [
  { key: "commission", label: "Commission", best: (ps) => maxBy(ps, "commission_value") },
  { key: "cookie_days", label: "Cookie Duration", fmt: (v) => `${v} days`, best: (ps) => maxBy(ps, "cookie_days") },
  { key: "epc", label: "Avg EPC", fmt: (v) => `$${v.toFixed(2)}`, best: (ps) => maxBy(ps, "epc") },
  { key: "aov", label: "Avg Order Value", fmt: (v) => (v ? `$${v.toLocaleString()}` : "N/A"), best: (ps) => maxBy(ps, "aov") },
  { key: "payout_threshold", label: "Payout Threshold", fmt: (v) => `$${v}`, best: (ps) => minBy(ps, "payout_threshold") },
  { key: "payout_frequency", label: "Payout Frequency" },
  { key: "conversion_rate", label: "Conversion Rate", fmt: (v) => `${v}%`, best: (ps) => maxBy(ps, "conversion_rate") },
  { key: "difficulty", label: "Approval Difficulty", fmt: (v) => v.charAt(0).toUpperCase() + v.slice(1) },
  { key: "beginner_friendly", label: "Beginner Friendly", fmt: (v) => (v ? "Yes" : "No") },
  { key: "recurring", label: "Recurring", fmt: (v) => (v ? "Yes" : "No") },
  { key: "payment_methods", label: "Payment Methods", fmt: (v) => v.join(", ") },
];

const maxBy = (ps, k) => ps.reduce((a, b) => (b[k] > a[k] ? b : a)).slug;
const minBy = (ps, k) => ps.reduce((a, b) => (b[k] < a[k] ? b : a)).slug;

export default function ComparePage() {
  const { items, remove, clear, setAll } = useCompare();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const urlApplied = useRef(false);

  useEffect(() => {
    const slugsParam = searchParams.get("slugs");
    if (slugsParam && !urlApplied.current) {
      urlApplied.current = true;
      const slugs = slugsParam.split(",").filter(Boolean).slice(0, 4);
      fetchCompare(slugs).then((d) => {
        if (d.programs.length) setAll(d.programs.map((p) => ({ slug: p.slug, name: p.name })));
        setSearchParams({}, { replace: true });
      }).catch(console.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (items.length === 0) { setPrograms([]); return; }
    setLoading(true);
    fetchCompare(items.map((i) => i.slug))
      .then((d) => setPrograms(d.programs))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [items]);

  const shareLink = () => {
    const url = `${window.location.origin}/compare?slugs=${items.map((i) => i.slug).join(",")}`;
    navigator.clipboard.writeText(url)
      .then(() => toast.success("Shareable link copied to clipboard"))
      .catch(() => toast.info(url));
  };

  const chartData = (key, label) => programs.map((p) => ({ name: p.name, [label]: p[key] }));

  if (items.length === 0) {
    return (
      <main className="max-w-4xl mx-auto px-4 py-24 text-center">
        <h1 className="font-heading text-4xl tracking-tight mb-4">Comparison Tool</h1>
        <p data-testid="compare-empty-state" className="text-slate-500 mb-8">You haven't added any programs yet. Add up to 4 programs from the directory to compare side-by-side.</p>
        <Link to="/programs" data-testid="compare-browse-link" className="inline-block px-8 py-3.5 rounded-full bg-slate-900 text-white font-medium hover:bg-slate-700 transition-colors duration-200">Browse Programs</Link>
      </main>
    );
  }

  return (
    <main data-testid="compare-page" className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
      <div className="flex items-end justify-between mb-10">
        <div>
          <p className="uppercase text-xs tracking-[0.2em] font-semibold text-slate-500 mb-2">Comparison Tool</p>
          <h1 className="font-heading text-4xl md:text-5xl tracking-tight">Side-by-side breakdown</h1>
        </div>
        <div className="flex items-center gap-3 print:hidden">
          <button onClick={shareLink} data-testid="compare-share-button" className="flex items-center gap-2 px-5 py-2.5 rounded-full border border-slate-300 text-sm font-medium text-slate-700 hover:border-emerald-600 hover:text-emerald-700 transition-colors duration-200">
            <Share2 className="w-4 h-4" /> Share link
          </button>
          <button onClick={() => window.print()} data-testid="compare-export-pdf-button" className="flex items-center gap-2 px-5 py-2.5 rounded-full border border-slate-300 text-sm font-medium text-slate-700 hover:border-emerald-600 hover:text-emerald-700 transition-colors duration-200">
            <Printer className="w-4 h-4" /> Export PDF
          </button>
          <button onClick={clear} data-testid="compare-clear-button" className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-rose-600 transition-colors duration-200">
            <Trash2 className="w-4 h-4" /> Clear all
          </button>
        </div>
      </div>

      {loading && programs.length === 0 ? (
        <p className="text-slate-400 py-16 text-center">Loading…</p>
      ) : (
        <>
          <div className="overflow-x-auto bg-white border border-slate-200 rounded-lg mb-12">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr>
                  <th className="text-left p-5 border-b border-r border-slate-200 w-48 uppercase text-xs tracking-[0.2em] text-slate-400 font-semibold">Metric</th>
                  {programs.map((p) => (
                    <th key={p.slug} className="p-5 border-b border-r border-slate-200 text-left min-w-[180px]">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <Link to={`/programs/${p.slug}`} className="font-heading font-semibold text-base hover:text-emerald-700 transition-colors duration-200">{p.name}</Link>
                          <p className="text-xs text-slate-500 font-normal mt-0.5">{p.niche_name}</p>
                        </div>
                        <button onClick={() => remove(p.slug)} data-testid={`compare-remove-${p.slug}`} aria-label={`Remove ${p.name}`} className="text-slate-400 hover:text-rose-600 transition-colors duration-200"><X className="w-4 h-4" /></button>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROWS.map((row) => {
                  const bestSlug = row.best && programs.length > 1 ? row.best(programs) : null;
                  return (
                    <tr key={row.key}>
                      <td className="p-5 border-b border-r border-slate-200 font-medium text-slate-500">{row.label}</td>
                      {programs.map((p) => (
                        <td key={p.slug} className={`p-5 border-b border-r border-slate-200 ${bestSlug === p.slug ? "bg-emerald-50 font-semibold text-emerald-800" : ""}`}>
                          <span className="flex items-center gap-1.5">
                            {row.fmt ? row.fmt(p[row.key]) : p[row.key]}
                            {bestSlug === p.slug && <Trophy className="w-3.5 h-3.5 text-emerald-600" />}
                          </span>
                        </td>
                      ))}
                    </tr>
                  );
                })}
                <tr>
                  <td className="p-5 border-r border-slate-200" />
                  {programs.map((p) => (
                    <td key={p.slug} className="p-5 border-r border-slate-200">
                      <a href={p.join_url} target="_blank" rel="noopener noreferrer" data-testid={`compare-join-${p.slug}`}
                        onClick={() => trackJoinClick(p.slug)}
                        className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-500 transition-colors duration-200">
                        Join <ExternalLink className="w-3 h-3" />
                      </a>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { key: "epc", label: "EPC ($)", title: "Earnings Per Click" },
              { key: "cookie_days", label: "Days", title: "Cookie Duration" },
              { key: "payout_threshold", label: "Threshold ($)", title: "Payout Threshold (lower is better)" },
            ].map(({ key, label, title }) => (
              <div key={key} data-testid={`compare-chart-${key}`} className="bg-white border border-slate-200 rounded-lg p-6">
                <p className="font-heading font-semibold mb-4">{title}</p>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={chartData(key, label)} margin={{ left: -15 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={50} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip cursor={{ fill: "#f1f5f9" }} />
                    <Bar dataKey={label} fill="#059669" radius={[4, 4, 0, 0]} maxBarSize={48} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ))}
          </div>
        </>
      )}
    </main>
  );
}
